/**
 * @fileoverview Checkout Form Persistence Management
 *
 * This file provides localStorage-based persistence for checkout form data,
 * ensuring user input is preserved across browser sessions and page reloads.
 *
 * Core Features:
 * - Automatic form data saving with timestamp tracking
 * - Data expiration (24-hour timeout) to prevent stale information
 * - Graceful error handling for localStorage failures
 * - Data integrity checks and automatic cleanup of corrupted data
 * - Integration with API data merging for seamless user experience
 *
 * Persistence Hook:
 * - useCheckoutFormPersistence: Main hook providing save/load/clear operations
 * - Automatic cleanup of expired or corrupted data
 * - Browser compatibility handling for localStorage access
 *
 * Utility Functions:
 * - mergePersistedWithApiData: Intelligent merging prioritizing user input
 * - Timestamp-based expiration management
 * - Error recovery with automatic data cleanup
 *
 * Data Strategy:
 * - User input takes priority over API defaults
 * - Automatic expiration prevents outdated information
 * - Silent fallback when localStorage is unavailable
 */

import { useEffect, useCallback } from "react";

/** Storage key for checkout form data in localStorage */
const CHECKOUT_FORM_STORAGE_KEY = "principleCheckoutFormData";

/**
 * Custom hook for managing checkout form persistence in localStorage
 *
 * Provides functions for saving, loading, and clearing form data with
 * automatic expiration and error handling.
 *
 * @returns Object containing persistence functions
 */
export const useCheckoutFormPersistence = () => {
  /**
   * Saves form data to localStorage with timestamp for expiration tracking
   *
   * @param formData - Form data object to persist
   */
  const saveFormData = useCallback((formData: Record<string, any>) => {
    try {
      const dataToSave = {
        formData,
        timestamp: Date.now(),
      };
      localStorage.setItem(CHECKOUT_FORM_STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.warn("Failed to save checkout form data to localStorage:", error);
    }
  }, []);

  /**
   * Loads form data from localStorage with expiration checking
   *
   * @returns Form data object or empty object if none/expired
   */
  const loadFormData = useCallback((): Record<string, any> => {
    try {
      const saved = localStorage.getItem(CHECKOUT_FORM_STORAGE_KEY);
      if (!saved) return {};

      const parsed = JSON.parse(saved);

      // Check if data is too old (24 hours)
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      if (parsed.timestamp && Date.now() - parsed.timestamp > maxAge) {
        // Data is too old, clear it
        clearFormData();
        return {};
      }

      return parsed.formData || {};
    } catch (error) {
      console.warn("Failed to load checkout form data from localStorage:", error);
      // Clear corrupted data
      clearFormData();
      return {};
    }
  }, []);

  /**
   * Clears all persisted form data from localStorage
   */
  const clearFormData = useCallback(() => {
    try {
      localStorage.removeItem(CHECKOUT_FORM_STORAGE_KEY);
    } catch (error) {
      console.warn("Failed to clear checkout form data from localStorage:", error);
    }
  }, []);

  /**
   * Checks if valid persisted data exists (not expired or corrupted)
   *
   * @returns True if valid persisted data exists, false otherwise
   */
  const hasPersistedData = useCallback((): boolean => {
    try {
      const saved = localStorage.getItem(CHECKOUT_FORM_STORAGE_KEY);
      if (!saved) return false;

      const parsed = JSON.parse(saved);

      // Check if data is too old (24 hours)
      const maxAge = 24 * 60 * 60 * 1000;
      if (parsed.timestamp && Date.now() - parsed.timestamp > maxAge) {
        clearFormData();
        return false;
      }

      return !!(parsed.formData && Object.keys(parsed.formData).length > 0);
    } catch (error) {
      console.warn("Failed to check for persisted checkout form data:", error);
      clearFormData();
      return false;
    }
  }, [clearFormData]);

  return {
    saveFormData,
    loadFormData,
    clearFormData,
    hasPersistedData,
  };
};

/**
 * Utility function to merge persisted data with API data, prioritizing user input
 *
 * This function combines user-entered form data (persisted) with default values
 * from the API, ensuring user input takes precedence over API defaults.
 *
 * @param persistedData - User-entered form data from localStorage
 * @param apiData - Default values from the checkout API
 * @returns Merged data object with user input prioritized
 */
export const mergePersistedWithApiData = (
  persistedData: Record<string, any>,
  apiData: any
): Record<string, any> => {
  const merged: Record<string, any> = {};

  // Start with persisted data (user input takes priority)
  Object.keys(persistedData).forEach((key) => {
    if (persistedData[key] !== undefined && persistedData[key] !== "") {
      merged[key] = persistedData[key];
    }
  });

  return merged;
};
